import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  input,
  linkedSignal,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { form, FormField } from '@angular/forms/signals';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatFormField, MatSelectModule } from '@angular/material/select';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';

import { WcgMenuOption } from '@wcg/frontend-framework';

import { ChipSelectType } from '../../types';
import { compareObjectsFn } from '../../utils';

@Component({
  selector: 'wcg-chip-selector',
  imports: [
    MatFormFieldModule,
    MatChipsModule,
    MatIconModule,
    MatSelectModule,
    FormField,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: ``,
  template: `
    <div class="w-full">
      <mat-form-field class="w-full" appearance="outline">
        <mat-label>{{ chipLabel() }}</mat-label>
        <mat-select
          [formField]="chipFormGroup.chips"
          [compareWith]="compareObjectsFn"
          (openedChange)="handleCdkPanelChange($event)"
          multiple
        >
          <mat-select-trigger>
            <mat-chip-grid #chipGrid aria-label="Fruit selection">
              @for (
                chip of chipFormGroup.chips().value();
                track chip.id;
                let chipIndex = $index
              ) {
                <mat-chip-row [removable]="false">
                  {{ chip.label }}
                </mat-chip-row>
              }
            </mat-chip-grid>
          </mat-select-trigger>
          @for (option of options(); track option.id) {
            <mat-option [value]="option">
              {{ option.label }}
            </mat-option>
          }
        </mat-select>
      </mat-form-field>
    </div>
  `,
})
export class ChipSelectorComponent {
  readonly options = input<WcgMenuOption[]>([]);
  readonly chipFormArray = input<WcgMenuOption[]>([]);
  readonly chipLabel = input<string>('');

  readonly selectedChips = output<WcgMenuOption[]>();

  readonly compareObjectsFn = compareObjectsFn;

  private readonly _matFormField = viewChild(MatFormField, {
    read: ElementRef,
  });

  private readonly _cdkPanelOpen = signal<boolean>(false);

  /*
   * @NOTE: The form group is used to manage the state of the chip select
   * */
  private readonly _chips = linkedSignal({
    computation: (chips) => ({
      chips: chips.length > 0 ? chips : [],
    }),
    source: this.chipFormArray,
  });
  readonly chipFormGroup = form<ChipSelectType>(this._chips);
  /**
   * @END
   */

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    const _target = event.target as HTMLElement;
    if (
      (this._matFormField()?.nativeElement &&
        !this._matFormField()?.nativeElement.contains(_target)) ||
      !this._cdkPanelOpen()
    ) {
      this.selectedChips.emit(this.chipFormGroup.chips().value());
    }
  }

  handleCdkPanelChange(isOpen: boolean) {
    this._cdkPanelOpen.set(isOpen);
  }
}
